import React, { useState, useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { 
  FaRupeeSign, 
  FaPercent, 
  FaCalendarAlt, 
  FaChevronDown, 
  FaChevronUp, 
  FaCalculator, 
  FaFileInvoiceDollar, 
  FaCoins 
} from 'react-icons/fa';
import './loan.css';

const LoanCalculator = () => {
  // Calculator state variables
  const [amount, setAmount] = useState(1000000); // Default ₹10 Lakhs
  const [rate, setRate] = useState(10.5); // Default 10.5% p.a.
  const [tenure, setTenure] = useState(60); // Default 60 Months / 5 Years
  const [tenureType, setTenureType] = useState('months'); // 'months' or 'years'
  const [showAmortization, setShowAmortization] = useState(false);
  const [scheduleType, setScheduleType] = useState('yearly'); // 'yearly' or 'monthly'

  // Automatically update limits depending on tenure type
  const tenureLimits = useMemo(() => {
    if (tenureType === 'years') {
      return { min: 1, max: 30, step: 1, label: 'Yr' };
    } else {
      return { min: 12, max: 360, step: 1, label: 'Mo' };
    }
  }, [tenureType]);

  // Handle tenure type toggle
  const handleTenureTypeChange = (type) => {
    if (type === tenureType) return;
    setTenureType(type);
    if (type === 'years') {
      // Convert months to years
      setTenure(Math.max(1, Math.min(30, Math.round(tenure / 12))));
    } else {
      // Convert years to months
      setTenure(Math.max(12, Math.min(360, tenure * 12)));
    }
  };

  // Safe numerical state handlers to handle manual input changes
  const handleAmountChange = (val) => {
    const numericValue = Math.min(Math.max(0, parseInt(val) || 0), 100000000); // Max 10 Crore
    setAmount(numericValue);
  };

  const handleRateChange = (val) => {
    let numericValue = parseFloat(val);
    if (isNaN(numericValue)) numericValue = 0;
    setRate(Math.min(30, Math.max(0, numericValue))); // Max 30%
  };

  const handleTenureChange = (val) => {
    const maxVal = tenureLimits.max;
    const minVal = tenureLimits.min;
    const numericValue = Math.min(Math.max(0, parseInt(val) || 0), maxVal);
    setTenure(numericValue);
  };

  // Perform core financial computations
  const results = useMemo(() => {
    const P = parseFloat(amount) || 0;
    const r = (parseFloat(rate) || 0) / 12 / 100;
    const n = tenureType === 'years' ? (parseInt(tenure) || 0) * 12 : (parseInt(tenure) || 0);

    if (P === 0 || n === 0) {
      return { emi: 0, totalInterest: 0, totalPayable: 0 };
    }

    let emi = 0;
    if (r === 0) {
      emi = P / n;
    } else {
      emi = P * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    }

    const totalPayable = emi * n;
    const totalInterest = totalPayable - P;

    return {
      emi: Math.round(emi),
      totalPayable: Math.round(totalPayable),
      totalInterest: Math.round(totalInterest)
    };
  }, [amount, rate, tenure, tenureType]);

  // Recharts visualization data
  const chartData = useMemo(() => {
    return [
      { name: 'Principal Amount', value: amount, color: '#ae0a0b' },
      { name: 'Interest Amount', value: results.totalInterest, color: '#fca5a5' }
    ];
  }, [amount, results.totalInterest]);

  // Formatter helpers
  const formatCurrency = (num) => {
    return '₹ ' + Math.round(num).toLocaleString('en-IN');
  };

  // Amortization Schedule Calculation
  const amortizationSchedule = useMemo(() => {
    const P = parseFloat(amount) || 0;
    const r = (parseFloat(rate) || 0) / 12 / 100;
    const n = tenureType === 'years' ? (parseInt(tenure) || 0) * 12 : (parseInt(tenure) || 0);

    if (P === 0 || n === 0) return [];

    let emi = 0;
    if (r === 0) {
      emi = P / n;
    } else {
      emi = P * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1);
    }

    let balance = P;
    const rawSchedule = [];

    for (let i = 1; i <= n; i++) {
      const interest = balance * r;
      const principal = emi - interest;
      balance = Math.max(0, balance - principal);

      rawSchedule.push({
        period: i,
        interest,
        principal,
        balance
      });
    }

    if (scheduleType === 'yearly') {
      const yearlyList = [];
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;
      let yearCount = 1;

      for (let i = 0; i < rawSchedule.length; i++) {
        yearlyInterest += rawSchedule[i].interest;
        yearlyPrincipal += rawSchedule[i].principal;

        if ((i + 1) % 12 === 0 || i === rawSchedule.length - 1) {
          yearlyList.push({
            label: `Year ${yearCount}`,
            interest: Math.round(yearlyInterest),
            principal: Math.round(yearlyPrincipal),
            balance: Math.round(rawSchedule[i].balance),
            totalPayment: Math.round(yearlyInterest + yearlyPrincipal)
          });
          yearlyInterest = 0;
          yearlyPrincipal = 0;
          yearCount++;
        }
      }
      return yearlyList;
    } else {
      return rawSchedule.map(item => ({
        label: `Month ${item.period}`,
        interest: Math.round(item.interest),
        principal: Math.round(item.principal),
        balance: Math.round(item.balance),
        totalPayment: Math.round(item.interest + item.principal)
      }));
    }
  }, [amount, rate, tenure, tenureType, scheduleType]);

  // Linear background logic for range sliders
  const getSliderBackground = (val, min, max) => {
    const percentage = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, #ae0a0b ${percentage}%, #e2e8f0 ${percentage}%)`;
  };

  return (
    <div className="loan-calculator-card">
      {/* Visual Accent Top Bar */}
      <div className="loan-accent-bar" />

      {/* Main Container Header */}
      <div className="loan-card-header">
        <div className="loan-header-title-box">
          <FaCalculator className="loan-header-icon" />
          <div>
            <h2 className="loan-title-main">EMI કેલ્ક્યુલેટર</h2>
            <p className="loan-tag-sub">EMI &amp; Amortization Loan Calculator</p>
          </div>
        </div>
        <div className="loan-tenure-toggle-wrapper">
          <span className="loan-toggle-label">સમયગાળો:</span>
          <div className="loan-toggle-pill">
            <button 
              className={`loan-toggle-btn ${tenureType === 'months' ? 'active' : ''}`}
              onClick={() => handleTenureTypeChange('months')}
            >
              મહિના (Months)
            </button>
            <button 
              className={`loan-toggle-btn ${tenureType === 'years' ? 'active' : ''}`}
              onClick={() => handleTenureTypeChange('years')}
            >
              વર્ષ (Years)
            </button>
          </div>
        </div>
      </div>

      <div className="loan-calculator-grid">
        {/* Left Side: Parameters Form */}
        <div className="loan-form-section">
          {/* Loan Amount Control */}
          <div className="loan-control-group">
            <div className="loan-control-header">
              <label className="loan-field-label">ધિરાણની રકમ (Loan Amount)</label>
              <div className="loan-input-pill">
                <span className="loan-addon-icon"><FaRupeeSign /></span>
                <input 
                  type="number" 
                  className="loan-numeric-input"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                />
              </div>
            </div>
            <input 
              type="range"
              className="loan-range-slider"
              min={50000}
              max={20000000} // 2 Crore
              step={10000}
              value={amount}
              style={{ background: getSliderBackground(amount, 50000, 20000000) }}
              onChange={(e) => handleAmountChange(e.target.value)}
            />
            <div className="loan-slider-indicators">
              <span>₹50 K</span>
              <span>₹2 Cr</span>
            </div>
          </div>

          {/* Interest Rate Control */}
          <div className="loan-control-group">
            <div className="loan-control-header">
              <label className="loan-field-label">વ્યાજનો દર (Interest Rate - p.a)</label>
              <div className="loan-input-pill">
                <input 
                  type="number" 
                  step="0.05"
                  className="loan-numeric-input text-right"
                  value={rate}
                  onChange={(e) => handleRateChange(e.target.value)}
                />
                <span className="loan-addon-icon right"><FaPercent /></span>
              </div>
            </div>
            <input 
              type="range"
              className="loan-range-slider"
              min={5}
              max={30}
              step={0.05}
              value={rate}
              style={{ background: getSliderBackground(rate, 5, 30) }}
              onChange={(e) => handleRateChange(e.target.value)}
            />
            <div className="loan-slider-indicators">
              <span>5 %</span>
              <span>30 %</span>
            </div>
          </div>

          {/* Tenure Duration Control */}
          <div className="loan-control-group">
            <div className="loan-control-header">
              <label className="loan-field-label">મુદત (Tenure - {tenureLimits.label === 'Yr' ? 'Years' : 'Months'})</label>
              <div className="loan-input-pill">
                <input 
                  type="number" 
                  className="loan-numeric-input text-right"
                  value={tenure}
                  onChange={(e) => handleTenureChange(e.target.value)}
                />
                <span className="loan-addon-icon right"><FaCalendarAlt /></span>
              </div>
            </div>
            <input 
              type="range"
              className="loan-range-slider"
              min={tenureLimits.min}
              max={tenureLimits.max}
              step={tenureLimits.step}
              value={tenure}
              style={{ background: getSliderBackground(tenure, tenureLimits.min, tenureLimits.max) }}
              onChange={(e) => handleTenureChange(e.target.value)}
            />
            <div className="loan-slider-indicators">
              <span>{tenureLimits.min} {tenureLimits.label}</span>
              <span>{tenureLimits.max} {tenureLimits.label}</span>
            </div>
          </div>

          {/* Monthly EMI Highlight Box */}
          <div className="loan-emi-banner">
            <div className="loan-emi-content">
              <span className="loan-emi-title">માસિક EMI (Monthly Installment)</span>
              <span className="loan-emi-val">{formatCurrency(results.emi)}</span>
            </div>           
          </div>
        </div>

        {/* Right Side: Charts and Breakdown Summary */}
        <div className="loan-summary-section">
          <div className="loan-summary-box">
            {/* Recharts Visualization */}
            <div className="loan-visualization-wrapper">
              <div className="loan-chart-container">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={85}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ 
                        borderRadius: '8px', 
                        border: 'none', 
                        boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                        fontFamily: 'inherit',
                        fontWeight: '700'
                      }}
                    />
                    <text
                      x="50%"
                      y="46%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="loan-center-chart-label"
                    >
                      ચૂકવવાપાત્ર કુલ રકમ
                    </text>
                    <text
                      x="50%"
                      y="56%"
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="loan-center-chart-value"
                    >
                      {formatCurrency(results.totalPayable)}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="loan-summary-table">
              <div className="loan-summary-row">
                <div className="loan-summary-meta">
                  <span className="loan-legend-dot principal" />
                  <span className="loan-summary-label">મુખ્ય રકમ (Principal)</span>
                </div>
                <span className="loan-summary-val font-bold">{formatCurrency(amount)}</span>
              </div>
              <div className="loan-summary-row">
                <div className="loan-summary-meta">
                  <span className="loan-legend-dot interest" />
                  <span className="loan-summary-label">કુલ વ્યાજ (Total Interest)</span>
                </div>
                <span className="loan-summary-val font-bold">{formatCurrency(results.totalInterest)}</span>
              </div>
              <div className="loan-summary-row highlight">
                <div className="loan-summary-meta">
                  <FaCoins className="loan-summary-icon" />
                  <span className="loan-summary-label">કુલ ચૂકવવાપાત્ર રકમ</span>
                </div>
                <span className="loan-summary-val font-extrabold">{formatCurrency(results.totalPayable)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Amortization Schedule Accordion Trigger */}
      <div className="loan-schedule-accordion">
        <button 
          className={`loan-accordion-trigger ${showAmortization ? 'expanded' : ''}`}
          onClick={() => setShowAmortization(!showAmortization)}
        >
          <div className="loan-accordion-header-left">
            <FaFileInvoiceDollar className="loan-accordion-icon" />
            <span>લોન ચુકવણી પત્રક (View Amortization Schedule)</span>
          </div>
          {showAmortization ? <FaChevronUp /> : <FaChevronDown />}
        </button>

        {showAmortization && (
          <div className="loan-accordion-content animate-fade-in">
            <div className="loan-schedule-controls">
              <div className="loan-schedule-toggle">
                <button 
                  className={`loan-schedule-toggle-btn ${scheduleType === 'yearly' ? 'active' : ''}`}
                  onClick={() => setScheduleType('yearly')}
                >
                  વાર્ષિક Breakdown (Yearly)
                </button>
                <button 
                  className={`loan-schedule-toggle-btn ${scheduleType === 'monthly' ? 'active' : ''}`}
                  onClick={() => setScheduleType('monthly')}
                >
                  માસિક Breakdown (Monthly)
                </button>
              </div>
            </div>

            <div className="loan-table-responsive">
              <table className="loan-schedule-table">
                <thead>
                  <tr>
                    <th>અવધિ (Period)</th>
                    <th>મુખ્ય હપ્તો (Principal)</th>
                    <th>વ્યાજ (Interest)</th>
                    <th>કુલ ચુકવણી (Total Installment)</th>
                    <th>બાકી રકમ (Balance Outstanding)</th>
                  </tr>
                </thead>
                <tbody>
                  {amortizationSchedule.map((row, idx) => {
                    const totalPaid = row.principal + row.interest;
                    const principalPercentage = (row.principal / totalPaid) * 100;
                    
                    return (
                      <tr key={idx} className="loan-table-row">
                        <td className="period-label">{row.label}</td>
                        <td>
                          <div className="loan-cell-breakdown">
                            <span>{formatCurrency(row.principal)}</span>
                            <div className="loan-mini-bar-track">
                              <div 
                                className="loan-mini-bar-fill principal" 
                                style={{ width: `${principalPercentage}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="loan-cell-breakdown">
                            <span>{formatCurrency(row.interest)}</span>
                            <div className="loan-mini-bar-track">
                              <div 
                                className="loan-mini-bar-fill interest" 
                                style={{ width: `${100 - principalPercentage}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="font-bold">{formatCurrency(row.totalPayment)}</td>
                        <td className="text-muted">{formatCurrency(row.balance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoanCalculator;
